/** @format */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
	addCompareProduct,
	removeCompareProduct,
	setCompareBarProducts,
	setCompareProductsAvailability,
	setIsCompareGridOpen
} from './compare.actions';
import { catchError, filter, Observable, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { selectCompareBarProducts, selectIsCompareGridOpen } from './compare.selectors';
import { ProductCatalogService, SessionStorageService } from '@shared/data-access/services';
import {
	CompareBarProduct,
	HeaderStoreSelection,
	ProductAvailabilityCollection
} from '@shared/data-access/interfaces';
import { isPlatformServer } from '@angular/common';
import { COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY } from '@compare/data-access';
import { changeHeaderServiceStoreInfo, selectUserStoreInfo } from '@state/data-access';

@Injectable()
export class CompareEffects {
	setCompareBarProducts$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(addCompareProduct),
				withLatestFrom(this.store.select(selectCompareBarProducts)),
				tap(([, compareBarProducts]) => {
					this.updateSessionStorage(compareBarProducts);
				})
			),
		{ dispatch: false }
	);

	removeCompareProduct$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(removeCompareProduct),
				withLatestFrom(this.store.select(selectCompareBarProducts)),
				tap(([action, compareBarProducts]) => {
					const productsAfterRemoval = compareBarProducts.filter(
						(cp) => cp.ecode !== action.ecode
					);
					this.store.dispatch(
						setCompareBarProducts({ compareBarProducts: productsAfterRemoval })
					);
					this.updateSessionStorage(productsAfterRemoval);
				})
			),
		{ dispatch: false }
	);

	/**
	 * @description
	 * When product list is updated, get the availability information and set data in state
	 */
	updateCompareProductAvailability$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(setIsCompareGridOpen, changeHeaderServiceStoreInfo),
				withLatestFrom(
					this.store.select(selectIsCompareGridOpen),
					this.store.select(selectCompareBarProducts),
					this.store.select(selectUserStoreInfo)
				),
				filter(
					([, isGridOpen, compareBarProducts]: [
						Action,
						boolean,
						CompareBarProduct[],
						HeaderStoreSelection
					]) => isGridOpen && compareBarProducts.length > 0
				),
				switchMap(
					([, , compareBarProducts, userSelectedStore]: [
						Action,
						boolean,
						CompareBarProduct[],
						HeaderStoreSelection
					]) =>
						this.productAvailability(
							compareBarProducts.map((product) => product.ecode),
							userSelectedStore
						)
				),
				tap((compareProductsAvailability: ProductAvailabilityCollection) => {
					if (compareProductsAvailability?.ecodeMessagingMap) {
						this.store.dispatch(
							setCompareProductsAvailability({ compareProductsAvailability })
						);
					}
				})
			),
		{ dispatch: false }
	);

	constructor(
		private readonly productCatalogService: ProductCatalogService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly actions$: Actions,
		private readonly store: Store,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {}

	private productAvailability(
		ecodes: string[],
		store: HeaderStoreSelection
	): Observable<ProductAvailabilityCollection | null | any[]> {
		if (isPlatformServer(this.platformId) || !ecodes?.length) {
			return of(null);
		}

		return this.productCatalogService
			.getAvailabilityData(
				ecodes,
				true,
				store?.zipcode || store?.zip,
				store?.store || store?.location
			)
			.pipe(catchError(() => of([])));
	}

	private updateSessionStorage(compareProducts: CompareBarProduct[]): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.set(
			COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
			JSON.stringify(compareProducts),
			''
		);
	}
}
